import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useCommunity } from "@/contexts/CommunityContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { InlineConfirmButton } from "@/components/shared/InlineConfirmButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  Dumbbell,
  Users,
  Wrench,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  {
    value: "SPORT",
    label: "Deporte",
    icon: Dumbbell,
    borderColor: "border-l-blue-500",
  },
  {
    value: "SOCIAL",
    label: "Social",
    icon: Users,
    borderColor: "border-l-purple-500",
  },
  {
    value: "SERVICE",
    label: "Servicio",
    icon: Wrench,
    borderColor: "border-l-amber-500",
  },
] as const;

type Category = (typeof CATEGORIES)[number]["value"];

interface AmenityFormData {
  name: string;
  category: Category;
  requiresApproval: boolean;
}

const defaultFormData: AmenityFormData = {
  name: "",
  category: "SPORT",
  requiresApproval: false,
};

export function ManageAmenitiesPage() {
  const utils = trpc.useUtils();
  const { selectedCommunityId } = useCommunity();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<{
    id: string;
    data: AmenityFormData;
  } | null>(null);
  const [newAmenity, setNewAmenity] =
    useState<AmenityFormData>(defaultFormData);

  const { data: adminCommunities, isLoading: adminCommunitiesLoading } =
    trpc.amenity.getAdminCommunities.useQuery();

  // Check if user has admin access to the selected community
  const hasAdminAccess = adminCommunities?.some(
    (c) => c.id === selectedCommunityId,
  );

  const { data: amenities, isLoading: amenitiesLoading } =
    trpc.amenity.getAmenitiesForAdmin.useQuery(
      { communityId: selectedCommunityId || "" },
      { enabled: !!selectedCommunityId && hasAdminAccess },
    );

  const createAmenity = trpc.amenity.createAmenity.useMutation({
    onSuccess: () => {
      utils.amenity.getAmenitiesForAdmin.invalidate();
      utils.amenity.getAmenities.invalidate();
      setIsCreateOpen(false);
      setNewAmenity(defaultFormData);
      toast.success("Amenity creado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateAmenity = trpc.amenity.updateAmenity.useMutation({
    onSuccess: () => {
      utils.amenity.getAmenitiesForAdmin.invalidate();
      utils.amenity.getAmenities.invalidate();
      setEditingAmenity(null);
      toast.success("Amenity actualizado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteAmenity = trpc.amenity.deleteAmenity.useMutation({
    onSuccess: () => {
      utils.amenity.getAmenitiesForAdmin.invalidate();
      utils.amenity.getAmenities.invalidate();
      toast.success("Amenity eliminado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = async () => {
    if (!selectedCommunityId || !newAmenity.name.trim()) return;

    createAmenity.mutate({
      communityId: selectedCommunityId,
      name: newAmenity.name,
      category: newAmenity.category,
      requiresApproval: newAmenity.requiresApproval,
    });
  };

  const handleUpdate = async () => {
    if (!editingAmenity || !editingAmenity.data.name.trim()) return;

    updateAmenity.mutate({
      amenityId: editingAmenity.id,
      name: editingAmenity.data.name,
      category: editingAmenity.data.category,
      requiresApproval: editingAmenity.data.requiresApproval,
    });
  };

  const handleDelete = async (amenityId: string) => {
    deleteAmenity.mutate({ amenityId });
  };

  // No admin access to any community
  if (
    !adminCommunitiesLoading &&
    (!adminCommunities || adminCommunities.length === 0)
  ) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-6">
          <Link
            to="/amenities"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Volver a Amenities
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">
            Gestionar Amenities
          </h1>
          <p className="text-muted-foreground">
            Creá y gestioná amenities de tu barrio.
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No tenés acceso de administrador a ningún barrio. Contactá a un
            superadmin para obtenerlo.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // User has admin access to some communities, but not the currently selected one
  if (!adminCommunitiesLoading && selectedCommunityId && !hasAdminAccess) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-6">
          <Link
            to="/amenities"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Volver a Amenities
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">
            Gestionar Amenities
          </h1>
          <p className="text-muted-foreground">
            Creá y gestioná amenities de tu barrio.
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No tenés acceso de administrador al barrio seleccionado. Seleccioná
            un barrio donde tengas permisos desde el menú lateral.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-6">
        <Link
          to="/amenities"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Volver a Amenities
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-bold md:text-3xl">Gestionar Amenities</h1>
        <p className="text-muted-foreground">
          Creá y gestioná amenities de tu barrio.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Amenities</CardTitle>
            <CardDescription>
              {amenities?.length || 0} amenities configurados
            </CardDescription>
          </div>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button disabled={!selectedCommunityId}>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo Amenity
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Amenity</DialogTitle>
                <DialogDescription>
                  Agregá un nuevo amenity para que los residentes puedan
                  reservar.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre</Label>
                  <Input
                    id="name"
                    placeholder="Cancha de tenis"
                    value={newAmenity.name}
                    onChange={(e) =>
                      setNewAmenity({ ...newAmenity, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      const isSelected = newAmenity.category === cat.value;
                      return (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() =>
                            setNewAmenity({
                              ...newAmenity,
                              category: cat.value,
                            })
                          }
                          className={`flex flex-col items-center gap-2 rounded-xl border-2 p-3 text-center transition-colors ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-muted hover:border-muted-foreground/50"
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-medium">
                            {cat.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requiresApproval"
                    checked={newAmenity.requiresApproval}
                    onCheckedChange={(checked) =>
                      setNewAmenity({
                        ...newAmenity,
                        requiresApproval: checked === true,
                      })
                    }
                  />
                  <Label htmlFor="requiresApproval" className="font-normal">
                    Requiere aprobación del administrador para reservas
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}
                  disabled={createAmenity.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={createAmenity.isPending || !newAmenity.name.trim()}
                >
                  {createAmenity.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    "Crear"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>

        <CardContent>
          {adminCommunitiesLoading || amenitiesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : amenities?.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              No hay amenities todavía. Creá uno para empezar.
            </p>
          ) : (
            <div className="space-y-3">
              {amenities?.map((amenity) => {
                const categoryConfig = CATEGORIES.find(
                  (c) => c.value === amenity.category,
                );
                const CategoryIcon = categoryConfig?.icon || Dumbbell;
                const borderColor = categoryConfig?.borderColor || "";

                return (
                  <div
                    key={amenity.id}
                    className={`flex items-center justify-between rounded-xl border border-l-4 p-4 ${borderColor}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                        <CategoryIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">{amenity.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {
                              CATEGORIES.find(
                                (c) => c.value === amenity.category,
                              )?.label
                            }
                          </Badge>
                          {amenity.requiresApproval && (
                            <Badge variant="outline" className="text-xs">
                              Requiere aprobación
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {amenity.reservationCount} reservas
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Dialog
                        open={editingAmenity?.id === amenity.id}
                        onOpenChange={(open) =>
                          setEditingAmenity(
                            open
                              ? {
                                  id: amenity.id,
                                  data: {
                                    name: amenity.name,
                                    category: amenity.category,
                                    requiresApproval: amenity.requiresApproval,
                                  },
                                }
                              : null,
                          )
                        }
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Editar amenity"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Editar Amenity</DialogTitle>
                            <DialogDescription>
                              Actualizá los detalles del amenity.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit-name">Nombre</Label>
                              <Input
                                id="edit-name"
                                value={editingAmenity?.data.name || ""}
                                onChange={(e) =>
                                  setEditingAmenity(
                                    editingAmenity
                                      ? {
                                          ...editingAmenity,
                                          data: {
                                            ...editingAmenity.data,
                                            name: e.target.value,
                                          },
                                        }
                                      : null,
                                  )
                                }
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Categoría</Label>
                              <div className="grid grid-cols-3 gap-3">
                                {CATEGORIES.map((cat) => {
                                  const Icon = cat.icon;
                                  const isSelected =
                                    editingAmenity?.data.category === cat.value;
                                  return (
                                    <button
                                      key={cat.value}
                                      type="button"
                                      onClick={() =>
                                        setEditingAmenity(
                                          editingAmenity
                                            ? {
                                                ...editingAmenity,
                                                data: {
                                                  ...editingAmenity.data,
                                                  category: cat.value,
                                                },
                                              }
                                            : null,
                                        )
                                      }
                                      className={`flex flex-col items-center gap-2 rounded-xl border-2 p-3 text-center transition-colors ${
                                        isSelected
                                          ? "border-primary bg-primary/5"
                                          : "border-muted hover:border-muted-foreground/50"
                                      }`}
                                    >
                                      <Icon className="h-5 w-5" />
                                      <span className="text-sm font-medium">
                                        {cat.label}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="edit-requiresApproval"
                                checked={editingAmenity?.data.requiresApproval}
                                onCheckedChange={(checked) =>
                                  setEditingAmenity(
                                    editingAmenity
                                      ? {
                                          ...editingAmenity,
                                          data: {
                                            ...editingAmenity.data,
                                            requiresApproval: checked === true,
                                          },
                                        }
                                      : null,
                                  )
                                }
                              />
                              <Label
                                htmlFor="edit-requiresApproval"
                                className="font-normal"
                              >
                                Requiere aprobación del administrador para
                                reservas
                              </Label>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setEditingAmenity(null)}
                              disabled={updateAmenity.isPending}
                            >
                              Cancelar
                            </Button>
                            <Button
                              onClick={handleUpdate}
                              disabled={updateAmenity.isPending}
                            >
                              {updateAmenity.isPending ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Guardando...
                                </>
                              ) : (
                                "Guardar"
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      <InlineConfirmButton
                        onConfirm={() => handleDelete(amenity.id)}
                        isPending={
                          deleteAmenity.isPending &&
                          deleteAmenity.variables?.amenityId === amenity.id
                        }
                        variant="outline"
                        size="icon"
                        aria-label="Eliminar amenity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </InlineConfirmButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
